"use client"

import { useState, useEffect, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { toast } from "react-hot-toast"
import { Calendar, X, ArrowDownCircle } from "lucide-react"
import DateRangePicker from '../components/ui/date-range-picker'
import { tokenReserveService } from "../apis/tokenReserveService"
import TokenReserveWithdrawModal from "../components/token-reserve-withdraw-modal"
import {
  getDefaultDateRangeToday,
  formatCstDateRangeLabel,
  formatCstDate,
  formatCstDateTime,
} from "../utils/cstDate"

export default function TokenReserves() {
  const [loading, setLoading] = useState(true);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetAllLoading, setResetAllLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  const [resetType, setResetType] = useState(null);
  const [tokenData, setTokenData] = useState({
    dealerTips: 0,
    waitressTips: 0,
    totalTokens: 0,
    reserveBalance: 0,
    lifetimeTaxCollected: 0,
    totalWithdrawn: 0,
  });

  const [dateRange, setDateRange] = useState(() => {
    const { start, end } = getDefaultDateRangeToday();
    return { start, end };
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [tokenHistory, setTokenHistory] = useState([]);
  const [withdrawalLogs, setWithdrawalLogs] = useState([]);

  const fetchTokenData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await tokenReserveService.getTokenReserve(
        formatCstDate(dateRange.start),
        formatCstDate(dateRange.end)
      );

      if (response.success) {
        const data = response.data.data;

        setTokenData({
          dealerTips: parseFloat(data.dealerTax || 0),
          waitressTips: parseFloat(data.waitressTax || 0),
          totalTokens: parseFloat(data.totalTax || 0),
          reserveBalance: parseFloat(data.reserveBalance || 0),
          lifetimeTaxCollected: parseFloat(data.lifetimeTaxCollected || 0),
          totalWithdrawn: parseFloat(data.totalWithdrawn || 0),
        });

        if (data.detailedRecords && Array.isArray(data.detailedRecords)) {
          const groupedByDate = data.detailedRecords.reduce((acc, record) => {
            const date = formatCstDate(record.date);

            if (!acc[date]) {
              acc[date] = {
                date: record.date,
                dealerTips: 0,
                waitressTips: 0,
                totalTokens: 0
              };
            }

            const taxAmount = parseFloat(record.tax || 0);

            if (record.source === 'Dealer') {
              acc[date].dealerTips += taxAmount;
            } else if (record.source === 'Waitress') {
              acc[date].waitressTips += taxAmount;
            }

            acc[date].totalTokens += taxAmount;

            return acc;
          }, {});

          const formattedHistory = Object.values(groupedByDate)
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .map((entry, index) => ({
              id: index + 1,
              date: entry.date,
              dealerTips: entry.dealerTips,
              waitressTips: entry.waitressTips,
              totalTokens: entry.totalTokens
            }));

          setTokenHistory(formattedHistory);
        } else {
          setTokenHistory([]);
        }

        setWithdrawalLogs(
          Array.isArray(data.withdrawals)
            ? data.withdrawals.map((entry) => ({
                id: entry.id,
                amount: parseFloat(entry.amount || 0),
                message: entry.message || "",
                withdrawnBy: entry.withdrawnBy || "Unknown",
                balanceBefore: parseFloat(entry.balanceBefore || 0),
                balanceAfter: parseFloat(entry.balanceAfter || 0),
                createdAt: entry.createdAt,
              }))
            : []
        );
      } else {
        throw new Error(response.error || "Failed to fetch token data");
      }
    } catch (error) {
      console.error("Error fetching token reserves:", error);
      toast.error("Failed to load token reserves data");
      setTokenData({
        dealerTips: 0,
        waitressTips: 0,
        totalTokens: 0,
        reserveBalance: 0,
        lifetimeTaxCollected: 0,
        totalWithdrawn: 0,
      });
      setTokenHistory([]);
      setWithdrawalLogs([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchTokenData();
  }, [fetchTokenData]);

  const closeResetModal = () => {
    setShowResetModal(false);
    setResetType(null);
  };

  const handleResetDailyTokens = async () => {
    try {
      setResetLoading(true);
      const response = await tokenReserveService.resetDailyTokens();

      if (response.success) {
        toast.success(response.message || "Daily tokens reset successfully!");
        await fetchTokenData();
      } else {
        throw new Error(response.error || "Failed to reset daily tokens");
      }
    } catch (error) {
      console.error("Error resetting daily tokens:", error);
      toast.error("Failed to reset daily tokens");
    } finally {
      setResetLoading(false);
      closeResetModal();
    }
  };

  const handleResetAllTokens = async () => {
    try {
      setResetAllLoading(true);
      const response = await tokenReserveService.resetAllTokens();

      if (response.success) {
        toast.success(response.message || "All tokens reset successfully!");
        await fetchTokenData();
      } else {
        throw new Error(response.error || "Failed to reset all tokens");
      }
    } catch (error) {
      console.error("Error resetting all tokens:", error);
      toast.error("Failed to reset all tokens");
    } finally {
      setResetAllLoading(false);
      closeResetModal();
    }
  };

  const confirmReset = () => {
    if (resetType === 'daily') {
      handleResetDailyTokens();
    } else if (resetType === 'all') {
      handleResetAllTokens();
    }
  };

  const handleWithdraw = async ({ amount, message }) => {
    try {
      setWithdrawSubmitting(true);
      const response = await tokenReserveService.createWithdrawal({ amount, message });

      if (response.success) {
        toast.success(response.message || "Withdrawal recorded successfully");
        setShowWithdrawModal(false);
        await fetchTokenData();
      } else {
        throw new Error(response.error || "Failed to record withdrawal");
      }
    } catch (error) {
      console.error("Error recording withdrawal:", error);
      toast.error(error.message || "Failed to record withdrawal");
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDateRange = () => formatCstDateRangeLabel(dateRange.start, dateRange.end);

  const handleDateRangeApply = (start, end) => {
    setDateRange({ start, end });
    setIsDatePickerOpen(false);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Token Reserves Management</h1>

          <div
            className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 px-3 py-1.5 rounded-md border border-gray-300 hover:border-blue-500 shadow-sm transition-colors duration-200 cursor-pointer"
            onClick={() => setIsDatePickerOpen(true)}
          >
            <Calendar className="w-4 h-4" />
            <span className="font-medium">{formatDateRange()}</span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="bg-green-50 border-green-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-green-700">Dealer Tips Tax</CardTitle>
                  <p className="text-xs text-green-600">Selected date range</p>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-green-600">${formatCurrency(tokenData.dealerTips)}</p>
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border-blue-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-blue-700">Server/waitress Tips Tax</CardTitle>
                  <p className="text-xs text-blue-600">Selected date range</p>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-blue-600">${formatCurrency(tokenData.waitressTips)}</p>
                </CardContent>
              </Card>

              <Card className="bg-yellow-50 border-yellow-200">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-lg text-yellow-700">Total Token Reserve</CardTitle>
                      <p className="text-xs text-yellow-600">Current available balance</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowWithdrawModal(true)}
                      className="inline-flex items-center gap-1.5 rounded-md bg-yellow-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-yellow-700"
                    >
                      <ArrowDownCircle className="h-4 w-4" />
                      Withdraw
                    </button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-yellow-600">${formatCurrency(tokenData.reserveBalance)}</p>
                  <div className="mt-3 space-y-1 text-xs text-yellow-700">
                    <p>Lifetime tax collected: ${formatCurrency(tokenData.lifetimeTaxCollected)}</p>
                    <p>Total withdrawn: ${formatCurrency(tokenData.totalWithdrawn)}</p>
                    <p>Period tax ({formatDateRange()}): ${formatCurrency(tokenData.totalTokens)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Daily Token Tax History</CardTitle>
                <p className="text-sm text-gray-600">Tax collected from Dealer and Server/waitress tips by date</p>
              </CardHeader>
              <CardContent>
                {tokenHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                          <th className="px-6 py-3">Date</th>
                          <th className="px-6 py-3">Dealer Tips Tax</th>
                          <th className="px-6 py-3">Server/waitress Tips Tax</th>
                          <th className="px-6 py-3">Total Tax</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tokenHistory.map((entry) => (
                          <tr key={entry.id} className="bg-white border-b hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium">{formatCstDate(entry.date, 'MMM D, YYYY')}</td>
                            <td className="px-6 py-4 text-green-600">${formatCurrency(entry.dealerTips)}</td>
                            <td className="px-6 py-4 text-blue-600">${formatCurrency(entry.waitressTips)}</td>
                            <td className="px-6 py-4 font-bold text-yellow-600">${formatCurrency(entry.totalTokens)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No token history available for the selected date range
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Token Reserve Withdrawal Logs</CardTitle>
                <p className="text-sm text-gray-600">
                  History of withdrawals from the token reserve with who deducted, when, and how much
                </p>
              </CardHeader>
              <CardContent>
                {withdrawalLogs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                          <th className="px-6 py-3">Date & Time</th>
                          <th className="px-6 py-3">Withdrawn By</th>
                          <th className="px-6 py-3">Amount</th>
                          <th className="px-6 py-3">Balance Before</th>
                          <th className="px-6 py-3">Balance After</th>
                          <th className="px-6 py-3">Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {withdrawalLogs.map((entry) => (
                          <tr key={entry.id} className="bg-white border-b hover:bg-gray-50 align-top">
                            <td className="px-6 py-4 font-medium whitespace-nowrap">
                              {formatCstDateTime(entry.createdAt)}
                            </td>
                            <td className="px-6 py-4">{entry.withdrawnBy}</td>
                            <td className="px-6 py-4 font-semibold text-red-600">
                              -${formatCurrency(entry.amount)}
                            </td>
                            <td className="px-6 py-4">${formatCurrency(entry.balanceBefore)}</td>
                            <td className="px-6 py-4 font-semibold text-yellow-700">
                              ${formatCurrency(entry.balanceAfter)}
                            </td>
                            <td className="px-6 py-4 max-w-md text-gray-700 whitespace-pre-wrap">
                              {entry.message}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No withdrawal logs yet
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {isDatePickerOpen && (
        <DateRangePicker
          isOpen={isDatePickerOpen}
          onClose={() => setIsDatePickerOpen(false)}
          onApply={handleDateRangeApply}
          initialStartDate={dateRange.start}
          initialEndDate={dateRange.end}
          initialSelectedRange="today"
        />
      )}

      <TokenReserveWithdrawModal
        isOpen={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        onSubmit={handleWithdraw}
        reserveBalance={tokenData.reserveBalance}
        submitting={withdrawSubmitting}
      />

      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Confirm Reset
              </h3>
              <button
                onClick={closeResetModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to reset {resetType === 'daily' ? 'daily' : 'all'} tokens?
                This action cannot be undone.
              </p>
              {resetType === 'all' && (
                <p className="text-red-600 text-sm mt-2 font-medium">
                  Warning: This will reset ALL token data in the database.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={closeResetModal}
                disabled={resetLoading || resetAllLoading}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                No
              </button>
              <button
                onClick={confirmReset}
                disabled={resetLoading || resetAllLoading}
                className={`
                  px-4 py-2 text-white rounded-md transition-all duration-200 flex items-center gap-2
                  ${resetLoading || resetAllLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : resetType === 'daily'
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-red-700 hover:bg-red-800'
                  }
                `}
              >
                {(resetLoading || resetAllLoading) && (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                )}
                <span>
                  {resetLoading || resetAllLoading ? 'Resetting...' : 'Yes'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
